import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import Papa from "papaparse";
import React, { FC, useState, useRef, ChangeEvent } from "react";

import GenericTable from "0.Mine/0.Components/Reusable/GenericTable";
import { UseHandleLeads } from "0.Mine/1.Hooks/CRM/Leads";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Lead, LeadState } from "0.Mine/3.Interfaces/lead";
import showConfirmationAlert from "layouts/pages/sweet-alerts/components/WithSuccessAttachedFunction/CancelSomething";

import { ImportConfiguration } from "./ImportConfiguration";
import { ImportMethodSelection } from "./ImportMethodSelection";
import { ImportReview } from "./ImportReview";

const steps: string[] = [
  "Seleziona metodo di importazione",
  "Configura Importazione",
  "Controlla e Importa",
];

const ImportLeads: FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [importMethod, setImportMethod] = useState<"csv" | "manual" | "">("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [manualLeads, setManualLeads] = useState<Partial<Lead>[]>([
    {
      id: 1,
      name: "",
      company: "",
      email: "",
      phone: "",
      state: LeadState.New,
      leadSource: "",
    },
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addLead } = UseHandleLeads();
  const { session } = useBusinessContext();

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleImportMethodChange = (method: "csv" | "manual") => {
    setImportMethod(method);
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setCsvFile(file);
      setIsLoading(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvData = e.target?.result as string;

        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          preview: 100,
          complete: (results) => {
            if (results.data && Array.isArray(results.data) && results.data.length > 0) {
              setTotalRowCount(results.data.length);
              const previewRows = results.data.slice(0, 20);
              const dataWithIds = previewRows.map((row: any, index: number) => ({
                id: index + 1,
                ...row,
              }));
              setPreviewData(dataWithIds);

              const initialMapping: Record<string, string> = {};
              const headers = results.meta.fields || Object.keys(results.data[0] || {});
              headers.forEach((header: string) => {
                const headerLower = header.toLowerCase();
                if (headerLower.includes("name") || headerLower.includes("full")) {
                  initialMapping[header] = "name";
                } else if (
                  headerLower.includes("company") ||
                  headerLower.includes("organization") ||
                  headerLower.includes("business")
                ) {
                  initialMapping[header] = "company";
                } else if (headerLower.includes("email") || headerLower.includes("e-mail")) {
                  initialMapping[header] = "email";
                } else if (
                  headerLower.includes("phone") ||
                  headerLower.includes("mobile") ||
                  headerLower.includes("cell")
                ) {
                  initialMapping[header] = "phone";
                } else if (headerLower.includes("state") || headerLower.includes("status")) {
                  initialMapping[header] = "state";
                } else if (
                  headerLower.includes("source") ||
                  headerLower.includes("channel") ||
                  headerLower.includes("origin")
                ) {
                  initialMapping[header] = "leadSource";
                } else if (headerLower.includes("note") || headerLower.includes("comment")) {
                  initialMapping[header] = "notes";
                } else if (
                  headerLower.includes("value") ||
                  headerLower.includes("amount") ||
                  headerLower.includes("deal")
                ) {
                  initialMapping[header] = "value";
                } else {
                  initialMapping[header] = "ignore";
                }
              });

              setColumnMapping(initialMapping);
              setIsLoading(false);
            } else {
              setPreviewData([]);
              setColumnMapping({});
              setIsLoading(false);
              alert(
                "The CSV file appears to be empty or invalid. Please check the file and try again."
              );
            }
          },
          error: (error) => {
            console.error("Error parsing CSV:", error);
            setIsLoading(false);
            alert("Error parsing CSV file. Please ensure it is a valid CSV format.");
          },
        });
      };

      reader.onerror = () => {
        setIsLoading(false);
        alert("Error reading file. Please try again.");
      };

      reader.readAsText(file);
    }
  };

  const transformDataForImport = () => {
    if (importMethod === "csv" && csvFile) {
      return new Promise<Partial<Lead>[]>((resolve, reject) => {
        setIsLoading(true);
        const reader = new FileReader();
        reader.onload = (e) => {
          const csvData = e.target?.result as string;
          Papa.parse(csvData, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              try {
                const transformedLeads = results.data.map((row: any, index: number) => {
                  const transformedLead: Partial<Lead> = { id: undefined };
                  Object.entries(columnMapping).forEach(([csvColumn, leadField]) => {
                    if (leadField !== "ignore" && row[csvColumn] !== undefined) {
                      if (leadField === "state") {
                        const stateValue = Object.values(LeadState).find(
                          (state) => state.toLowerCase() === String(row[csvColumn]).toLowerCase()
                        );
                        (transformedLead as any)[leadField] = stateValue || LeadState.New;
                      } else if (leadField === "value") {
                        const valueString = String(row[csvColumn]).replace(/[$£€,]/g, "");
                        (transformedLead as any)[leadField] = parseFloat(valueString) || 0;
                      } else {
                        (transformedLead as any)[leadField] = row[csvColumn];
                      }
                    }
                  });
                  return {
                    ...transformedLead,
                    state: transformedLead.state || LeadState.New,
                  };
                });
                setIsLoading(false);
                resolve(transformedLeads);
              } catch (error) {
                setIsLoading(false);
                reject(error);
              }
            },
            error: (error) => {
              setIsLoading(false);
              reject(error);
            },
          });
        };

        reader.onerror = () => {
          setIsLoading(false);
          reject(new Error("Error reading file"));
        };

        reader.readAsText(csvFile);
      });
    } else if (importMethod === "manual") {
      return Promise.resolve(manualLeads);
    } else {
      return Promise.resolve([]);
    }
  };

  const handleMappingChange = (csvColumn: string, leadField: string) => {
    setColumnMapping({
      ...columnMapping,
      [csvColumn]: leadField,
    });
  };

  const handleAddManualLead = () => {
    setManualLeads([
      ...manualLeads,
      {
        id: manualLeads.length + 1,
        name: "",
        company: "",
        email: "",
        phone: "",
        state: LeadState.New,
        leadSource: "",
      },
    ]);
  };

  const handleManualLeadChange = (index: number, field: keyof Lead, value: any) => {
    const updatedLeads = [...manualLeads];
    updatedLeads[index] = {
      ...updatedLeads[index],
      [field]: value,
    };
    setManualLeads(updatedLeads);
  };

  const handleRemoveManualLead = (index: number) => {
    const updatedLeads = manualLeads.filter((_, i) => i !== index);
    setManualLeads(updatedLeads);
  };

  const handleImportSubmit = async () => {
    try {
      setIsLoading(true);
      const leadsToImport = await transformDataForImport();
      console.log("Leads ready for import:", leadsToImport);
      setTimeout(() => {
        setIsLoading(false);
        setActiveStep(0);
        setImportMethod("");
        setCsvFile(null);
        setPreviewData([]);
        setColumnMapping({});
        setManualLeads([
          {
            id: 1,
            name: "",
            company: "",
            email: "",
            phone: "",
            state: LeadState.New,
            leadSource: "",
          },
        ]);
      }, 1000);

      const confirm = await showConfirmationAlert({
        title: "Import Leads?",
        text: "Are you sure you want to import these leads? This action is irreversible",
        icon: "warning",
        confirmButtonText: "Yes, Import",
        cancelButtonText: "No, go back",
        successTitle: "Leads imported",
        successText: "It may take a moment before the leads appear on your software",
        onConfirm: async () => {
          // Perform the actual import
          const leads = await addLead(session.selectedBusiness.id, leadsToImport);
          console.log("Leads added:", leads);
        },
      });
    } catch (error) {
      setIsLoading(false);
      console.error("Error importing leads:", error);
      alert("There was an error importing your leads. Please try again.");
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ImportMethodSelection
            importMethod={importMethod}
            onSelectMethod={handleImportMethodChange}
          />
        );
      case 1:
        return (
          <ImportConfiguration
            importMethod={importMethod as "csv" | "manual"}
            csvFile={csvFile}
            onFileUpload={handleFileUpload}
            fileInputRef={fileInputRef}
            columnMapping={columnMapping}
            onMappingChange={handleMappingChange}
            previewData={previewData}
            totalRowCount={totalRowCount}
            isLoading={isLoading}
            manualLeads={manualLeads}
            onManualLeadChange={handleManualLeadChange}
            onAddManualLead={handleAddManualLead}
            onRemoveManualLead={handleRemoveManualLead}
          />
        );
      case 2:
        return (
          <ImportReview
            importMethod={importMethod as "csv" | "manual"}
            csvFile={csvFile}
            previewData={previewData}
            manualLeads={manualLeads}
          />
        );
      default:
        return null;
    }
  };

  const isNextDisabled = () => {
    if (activeStep === 0) {
      return importMethod === "";
    }
    if (activeStep === 1) {
      if (importMethod === "csv") {
        return !csvFile;
      }
      if (importMethod === "manual") {
        return manualLeads.some((lead) => !lead.name || !lead.company || !lead.email);
      }
    }
    return false;
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold" }}>
        Importa Leads
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>
      <Box sx={{ mb: 4 }}>{renderStepContent()}</Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0}>
          Indietro
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" color="primary" onClick={handleImportSubmit}>
            Importa Leads
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            Avanti
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ImportLeads;
